import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Calculator,
  Check,
  CheckCircle2,
  Clock,
  FileCheck2,
  Gauge,
  Loader2,
  Lock,
  PauseCircle,
  PlayCircle,
  Printer,
  ShieldAlert,
} from "lucide-react";

import { ApiError, formatCLP, formatDateTime } from "../../lib/api";
import { cantidad } from "../../lib/format";
import { useAvisoSalida } from "../../lib/useAvisoSalida";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  Textarea,
} from "../../components/ui";
import {
  calcularCierre,
  cerrarCierre,
  descargarBoletasPdf,
  enviarARevision,
  liberarRevision,
  listarValidaciones,
  obtenerCierre,
  prevalidarCierre,
  type CierreMensualRead,
  type CierreValidacionDetalleRead,
  type EstadoCierre,
} from "./api";
import { ETIQUETA_ESTADO, EstadoBadge, MESES } from "./estado";
import { DtePanelCierre } from "../dte/DtePanelCierre";
import { FaltantesPanel } from "./FaltantesPanel";
import { TareaCierrePanel } from "./TareaCierrePanel";
import { enCurso, registrarTareaLanzada, useTareaCierre } from "./tareas";
import { GrupoAvisos } from "./GrupoAvisos";
import { descuentoCLP, describirMotivoBloqueo } from "./textos";
import { PASOS_PERIODO, pasoActual } from "./pasos";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { listarRutas } from "../rutas/api";
import { listarSectores } from "../sectores/api";

/** Estados en los que las boletas ya tienen número y se pueden imprimir. */
const ESTADOS_CON_BOLETAS: ReadonlySet<EstadoCierre> = new Set([
  "FACTURADO",
  "HOLD",
  "CERRADO",
]);

/** Códigos de validación que hablan de una casa sin lectura (no hay lectura que abrir). */
const CODIGO_SIN_LECTURA = "BLQ-001";
const CODIGO_ESTIMADA = "ALT-001";
const CODIGO_REVISION = "ALT-002";

/** Cómo se titula cada tipo de observación de la revisión (por código). */
const GRUPO_VALIDACION: Record<string, { titulo: string; descripcion: string }> = {
  [CODIGO_SIN_LECTURA]: {
    titulo: "casas sin lectura",
    descripcion: "Ingresa su lectura o estímala por término medio en el panel de arriba.",
  },
  [CODIGO_REVISION]: {
    titulo: "lecturas marcadas para revisión",
    descripcion: "Consumo fuera de lo normal: confirma la lectura antes de calcular.",
  },
  [CODIGO_ESTIMADA]: {
    titulo: "lecturas estimadas por término medio",
    descripcion: "No impiden avanzar; la boleta sale marcada como “Término medio”.",
  },
};

interface GrupoValidacion {
  codigo: string;
  titulo: string;
  descripcion: string | undefined;
  bloquea: boolean;
  items: CierreValidacionDetalleRead[];
}

/**
 * Una sección por código, con lo que impide avanzar primero, luego lo que
 * pide revisión y al final los avisos informativos (estimadas).
 */
function agruparValidaciones(validaciones: CierreValidacionDetalleRead[]): GrupoValidacion[] {
  const porCodigo = new Map<string, GrupoValidacion>();
  for (const v of validaciones) {
    let g = porCodigo.get(v.codigo);
    if (!g) {
      const meta = GRUPO_VALIDACION[v.codigo];
      g = {
        codigo: v.codigo,
        titulo: meta?.titulo ?? `observaciones (${v.codigo})`,
        descripcion: meta?.descripcion,
        bloquea: false,
        items: [],
      };
      porCodigo.set(v.codigo, g);
    }
    g.bloquea ||= v.bloquea;
    g.items.push(v);
  }
  const peso = (g: GrupoValidacion): number =>
    g.bloquea ? 0 : g.codigo === CODIGO_REVISION ? 1 : g.codigo === CODIGO_ESTIMADA ? 3 : 2;
  return [...porCodigo.values()].sort((a, b) => peso(a) - peso(b));
}

/**
 * El texto de una observación sin repetir: el backend arma la descripción
 * con el motivo adentro y además lo manda en `valor`.
 */
function textoValidacion(v: CierreValidacionDetalleRead): string {
  if (v.valor && !v.descripcion.includes(v.valor)) return `${v.descripcion} (${v.valor})`;
  return v.descripcion;
}

function ValidacionFila({
  validacion: v,
  contrato: c,
  lecturasHref,
  desde,
}: {
  validacion: CierreValidacionDetalleRead;
  contrato: ContratoOperativo | undefined;
  lecturasHref: (c: ContratoOperativo) => string;
  desde: { etiqueta: string; ruta: string };
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 text-sm">
      <div className="min-w-0 flex-1">
        <div className="text-neutral-900">
          {c ? (
            <>
              {c.socio_nombre}
              <span className="ml-1 text-xs text-neutral-600">
                · {c.arranque_codigo ?? ""} {c.arranque_direccion}
              </span>
            </>
          ) : v.contrato_id != null ? (
            `Contrato ${v.contrato_id}`
          ) : (
            "—"
          )}
        </div>
        <div className="text-xs text-neutral-600" title={v.codigo}>
          {textoValidacion(v)}
          {v.resuelto && (
            <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
              Resuelta
            </span>
          )}
        </div>
      </div>
      {c && (
        <Link
          to={lecturasHref(c)}
          state={{ desde }}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
        >
          {v.codigo === CODIGO_SIN_LECTURA ? "Ver en lecturas" : "Ir a la lectura"}
          <ArrowRight className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

const VOLVER_CIERRES = { etiqueta: "Cierres", ruta: "/cierres" };

const ID_PANEL_IMPRIMIR = "imprimir-boletas";

const TEXTO_ESPERA =
  "Esto puede tardar hasta 2 minutos. No cierres ni recargues la página.";

/** Spinner + texto para el botón de una operación en curso. */
function EnCurso({ texto }: { texto: string }): JSX.Element {
  return (
    <>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {texto}
    </>
  );
}

/** Aviso bajo los botones mientras corre una operación larga. */
function AvisoEspera({ texto = TEXTO_ESPERA }: { texto?: string }): JSX.Element {
  return (
    <p
      role="status"
      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <span>{texto}</span>
    </p>
  );
}

export function CierreDetailPage(): JSX.Element {
  const { cierreId: cierreIdParam } = useParams<{ cierreId: string }>();
  const cierreId = Number(cierreIdParam);

  const cierreQ = useQuery({
    queryKey: ["cierre", cierreId],
    queryFn: () => obtenerCierre(cierreId),
    enabled: Number.isFinite(cierreId),
  });

  const validacionesQ = useQuery({
    queryKey: ["cierre-validaciones", cierreId],
    queryFn: () => listarValidaciones(cierreId, false),
    enabled: Number.isFinite(cierreId),
  });

  // Para mostrar el socio y la casa en la tabla de validaciones (en vez del
  // id crudo del contrato) y armar el enlace a sus lecturas.
  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: fetchContratos });
  const contratoPorId = useMemo(() => {
    const m = new Map<number, ContratoOperativo>();
    for (const c of contratosQ.data ?? []) m.set(c.id, c);
    return m;
  }, [contratosQ.data]);

  if (!Number.isFinite(cierreId)) {
    return <p className="text-sm text-rose-700">Cierre inválido.</p>;
  }

  return (
    <div className="space-y-6">
      {!cierreQ.data && <PageHeader title="Cierre" volver={VOLVER_CIERRES} />}
      {cierreQ.isPending && <Cargando />}
      {cierreQ.isError && (
        <ErrorCarga
          error={cierreQ.error}
          mensaje="No se pudo cargar el cierre."
          onReintentar={() => void cierreQ.refetch()}
        />
      )}

      {cierreQ.data && (
        <CierreDetalle
          cierre={cierreQ.data}
          validaciones={validacionesQ.data ?? []}
          validacionesLoading={validacionesQ.isPending}
          contratoPorId={contratoPorId}
        />
      )}
    </div>
  );
}

interface DetalleProps {
  cierre: CierreMensualRead;
  validaciones: CierreValidacionDetalleRead[];
  validacionesLoading: boolean;
  contratoPorId: Map<number, ContratoOperativo>;
}

function CierreDetalle({
  cierre,
  validaciones,
  validacionesLoading,
  contratoPorId,
}: DetalleProps): JSX.Element {
  const validacionesPendientes = validaciones.filter((v) => !v.resuelto);
  const bloqueantes = validacionesPendientes.filter((v) => v.bloquea);

  const sectoresQ = useQuery({ queryKey: ["sectores"], queryFn: listarSectores });
  const sectorNombre =
    cierre.sector_id == null
      ? null
      : sectoresQ.data?.find((s) => s.id === cierre.sector_id)?.nombre ?? null;

  const periodo = `${MESES[cierre.mes - 1]} ${cierre.anio}`;
  const desde = { etiqueta: `Cierre ${periodo}`, ruta: `/cierres/${cierre.id}` };
  // Nunca revisado: los contadores siguen en cero porque la revisión es la
  // que los calcula (un cierre revisado tiene al menos sus contratos).
  const sinRevisar =
    (cierre.estado === "ABIERTO" || cierre.estado === "EN_PREVALIDACION") &&
    cierre.lecturas_esperadas === 0 &&
    cierre.total_contratos === 0;

  return (
    <>
      <PageHeader
        title={`Cierre ${periodo}`}
        subtitle={
          cierre.sector_id == null ? "Todos los sectores" : `Sector ${sectorNombre ?? "…"}`
        }
        volver={VOLVER_CIERRES}
        actions={
          <div className="flex flex-col items-end gap-1">
            <EstadoBadge estado={cierre.estado} />
            {cierre.fecha_cierre && (
              <div className="text-xs text-neutral-600">
                Cerrado el {formatDateTime(cierre.fecha_cierre)}
              </div>
            )}
          </div>
        }
      />
      <div className="-mt-3 space-y-1">
        {cierre.observaciones && (
          <p className="text-sm text-neutral-600">{cierre.observaciones}</p>
        )}
        <Link
          to={`/lecturas?mes=${cierre.mes}&anio=${cierre.anio}`}
          state={{ desde }}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800"
        >
          <Gauge className="h-3.5 w-3.5" strokeWidth={1.5} />
          Ver lecturas del periodo
        </Link>
      </div>

      <PasosPeriodo estado={cierre.estado} />

      {cierre.motivo_bloqueo && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <strong>Por qué no avanza:</strong> {describirMotivoBloqueo(cierre.motivo_bloqueo)}
        </div>
      )}

      {cierre.estado === "HOLD" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>En revisión</strong>
          {cierre.fecha_hold && <> desde el {formatDateTime(cierre.fecha_hold)}</>}
          {cierre.motivo_hold && <>: {cierre.motivo_hold}</>}
        </div>
      )}

      {/* Estimar, calcular y emitir corren en el servidor: el avance (o el
          error, con reintentar) se ve acá aunque el operador salga y vuelva. */}
      <TareaCierrePanel cierreId={cierre.id} />

      {/* El próximo paso va antes que el detalle: con cientos de casas sin
          lectura, el panel de faltantes lo empujaba fuera de la pantalla. */}
      <ProximoPaso cierre={cierre} bloqueantes={bloqueantes.length} />

      {/* Faltantes: mientras el período admite lecturas, la salida al
          bloqueo es estimar por término medio. */}
      {(cierre.estado === "ABIERTO" || cierre.estado === "BLOQUEADO") && (
        <FaltantesPanel cierre={cierre} />
      )}

      {/* Impresión de boletas: sólo cuando ya están emitidas */}
      {ESTADOS_CON_BOLETAS.has(cierre.estado) && (
        <BoletasImpresasPanel cierre={cierre} />
      )}

      {/* Documentos tributarios ante el SII: sólo si el comité emite DTE. */}
      <DtePanelCierre cierre={cierre} />

      {/* Counters: se calculan al revisar las lecturas. Antes de la primera
          revisión todos valen 0 y "Faltantes 0" en verde mentía. */}
      <section className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Counter label="Contratos" value={sinRevisar ? null : cierre.total_contratos} />
          <Counter
            label="Lecturas esperadas"
            value={sinRevisar ? null : cierre.lecturas_esperadas}
          />
          <Counter
            label="Ingresadas"
            value={sinRevisar ? null : cierre.lecturas_ingresadas}
            tone={
              sinRevisar
                ? "info"
                : cierre.lecturas_ingresadas === cierre.lecturas_esperadas
                  ? "ok"
                  : "warn"
            }
          />
          <Counter
            label="Faltantes"
            value={sinRevisar ? null : cierre.lecturas_faltantes}
            tone={sinRevisar ? "info" : cierre.lecturas_faltantes > 0 ? "warn" : "ok"}
          />
          <Counter
            label="Estimadas"
            value={sinRevisar ? null : cierre.lecturas_estimadas}
            tone="muted"
          />
        </div>
        {sinRevisar && (
          <p className="text-xs text-neutral-600">Se calcula al revisar las lecturas.</p>
        )}
      </section>

      {/* Totales monetarios */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MoneyCard label="Total m³" value={`${cantidad(cierre.total_m3)} m³`} />
        <MoneyCard
          label="Total facturado"
          value={formatCLP(cierre.total_facturado)}
          highlight
        />
        <MoneyCard label="Total subsidios" value={descuentoCLP(cierre.total_subsidios)} />
        <MoneyCard label="Total multas" value={formatCLP(cierre.total_multas)} />
      </section>

      {/* Validaciones */}
      <section className="bg-white rounded-lg border border-neutral-200">
        <header className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-2">
          <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
          <h2 className="text-sm font-semibold text-neutral-900">
            Revisión de lecturas
          </h2>
          {validaciones.length > 0 && (
            <span className="text-xs text-neutral-600">
              {validacionesPendientes.length} pendiente(s) · {bloqueantes.length}{" "}
              impide(n) avanzar
            </span>
          )}
        </header>
        <div className="p-4">
          {validacionesLoading ? (
            <Cargando inline />
          ) : validaciones.length === 0 ? (
            cierre.estado === "ABIERTO" ? (
              <p className="text-sm text-neutral-600">
                Todavía no se revisan las lecturas de este periodo.
              </p>
            ) : (
              <p className="text-sm text-emerald-700">
                Sin observaciones: todas las lecturas pasaron la revisión.
              </p>
            )
          ) : (
            <div className="space-y-2">
              {agruparValidaciones(validaciones).map((g) => (
                <GrupoAvisos
                  key={g.codigo}
                  titulo={g.titulo}
                  descripcion={g.descripcion}
                  cantidad={g.items.length}
                  tono={g.bloquea ? "rose" : g.codigo === CODIGO_REVISION ? "amber" : "slate"}
                  etiqueta={g.bloquea ? "Impide avanzar" : "Aviso"}
                  abiertoInicial={g.bloquea || g.codigo === CODIGO_REVISION}
                  items={g.items}
                  itemKey={(v) => v.id}
                  renderItem={(v) => (
                    <ValidacionFila
                      validacion={v}
                      contrato={
                        v.contrato_id != null ? contratoPorId.get(v.contrato_id) : undefined
                      }
                      lecturasHref={(c) =>
                        `/lecturas?mes=${cierre.mes}&anio=${cierre.anio}&q=${encodeURIComponent(
                          c.arranque_codigo ?? c.socio_nombre,
                        )}`
                      }
                      desde={desde}
                    />
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/** Barra de pasos del periodo: marca lo hecho, el paso en curso y lo que falta. */
function PasosPeriodo({ estado }: { estado: EstadoCierre }): JSX.Element {
  const actual = pasoActual(estado);
  return (
    <nav aria-label="Pasos del periodo" className="bg-white rounded-lg border border-neutral-200 px-4 py-3">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-xs">
        {PASOS_PERIODO.map((nombre, i) => {
          const hecho = i < actual;
          const enCurso = i === actual;
          return (
            <li key={nombre} className="flex items-center gap-2">
              <span
                aria-current={enCurso ? "step" : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
                  enCurso
                    ? "bg-brand-100 text-brand-800 ring-1 ring-inset ring-brand-300"
                    : hecho
                      ? "text-emerald-700"
                      : "text-neutral-600"
                }`}
              >
                {hecho ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                ) : (
                  <span
                    className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-xs ${
                      enCurso ? "bg-brand-700 text-white" : "bg-neutral-200 text-neutral-700"
                    }`}
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                )}
                {nombre}
                {hecho && <span className="sr-only">(hecho)</span>}
                {enCurso && <span className="sr-only">(paso actual)</span>}
              </span>
              {i < PASOS_PERIODO.length - 1 && (
                <span className="text-neutral-400" aria-hidden="true">
                  ›
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * El siguiente movimiento del periodo. Siempre hay UN botón principal; lo
 * demás va como secundario. "Revisar y emitir" ya no tiene atajo: emitir
 * sin pasar por la vista previa saltaba la única revisión de montos.
 */
function ProximoPaso({
  cierre,
  bloqueantes,
}: {
  cierre: CierreMensualRead;
  bloqueantes: number;
}): JSX.Element | null {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmCerrar, setConfirmCerrar] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);

  const invalidar = (): void => {
    void qc.invalidateQueries({ queryKey: ["cierre", cierre.id] });
    void qc.invalidateQueries({ queryKey: ["cierre-validaciones", cierre.id] });
    void qc.invalidateQueries({ queryKey: ["cierres", cierre.mes, cierre.anio] });
  };

  const accion = useMutation({
    mutationFn: (fn: (id: number) => Promise<unknown>) => fn(cierre.id),
    onSuccess: () => invalidar(),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo completar el paso");
    },
  });

  function ejecutar(
    fn: (id: number) => Promise<unknown>,
    ok: string,
    alTerminar?: () => void,
  ): void {
    accion.mutate(fn, {
      onSuccess: () => toast.success(ok),
      onSettled: () => alTerminar?.(),
    });
  }

  function irAImprimir(): void {
    const panel = document.getElementById(ID_PANEL_IMPRIMIR);
    panel?.scrollIntoView({ behavior: "smooth", block: "start" });
    panel?.focus({ preventScroll: true });
  }

  // Calcular corre en segundo plano: el POST sólo la lanza y el avance se
  // ve en TareaCierrePanel. Mientras corre una tarea, nada más se toca.
  const tareaQ = useTareaCierre(cierre.id);
  const lanzarCalculo = useMutation({
    mutationFn: () => calcularCierre(cierre.id),
    onSuccess: (tarea) => registrarTareaLanzada(qc, tarea),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo iniciar el cálculo");
      void tareaQ.refetch();
    },
  });

  const ocupado = accion.isPending || lanzarCalculo.isPending || enCurso(tareaQ.data);
  // Revisar y cerrar recorren todo el padrón en el mismo request: con 2.000
  // casas tardan, y si el operador recarga a mitad no ve el resultado. El
  // aviso de salida sólo cubre lo que corre en el request, no las tareas.
  useAvisoSalida(accion.isPending || lanzarCalculo.isPending);
  const contratos = cierre.total_contratos > 0 ? cierre.total_contratos : null;
  const deCasas = contratos ? ` ${contratos.toLocaleString("es-CL")}` : "";

  let texto: React.ReactNode = null;
  let botones: React.ReactNode = null;

  switch (cierre.estado) {
    case "ABIERTO":
      texto =
        "Cuando estén ingresadas las lecturas del mes, revísalas: el sistema avisa de las casas sin lectura y de los consumos raros.";
      botones = (
        <Button icon={accion.isPending ? undefined : ShieldAlert} disabled={ocupado} onClick={() => ejecutar(prevalidarCierre, "Lecturas revisadas")}>
          {accion.isPending ? <EnCurso texto="Revisando lecturas…" /> : "Revisar lecturas"}
        </Button>
      );
      break;
    case "BLOQUEADO":
      texto =
        bloqueantes > 0
          ? `Hay ${bloqueantes.toLocaleString("es-CL")} caso(s) que impiden avanzar. Ingresa o estima las lecturas que faltan y vuelve a revisar.`
          : "Corrige lo indicado y vuelve a revisar las lecturas.";
      botones = (
        <Button icon={accion.isPending ? undefined : ShieldAlert} disabled={ocupado} onClick={() => ejecutar(prevalidarCierre, "Lecturas revisadas")}>
          {accion.isPending ? <EnCurso texto="Revisando lecturas…" /> : "Volver a revisar lecturas"}
        </Button>
      );
      break;
    case "PREVALIDADO":
      texto = "Las lecturas están revisadas. Calcula los montos de cada boleta.";
      botones = (
        <Button icon={ocupado ? undefined : Calculator} disabled={ocupado} onClick={() => lanzarCalculo.mutate()}>
          {lanzarCalculo.isPending ? (
            <EnCurso texto="Iniciando el cálculo…" />
          ) : enCurso(tareaQ.data) ? (
            <EnCurso texto={`Calculando${deCasas} boletas…`} />
          ) : (
            "Calcular boletas"
          )}
        </Button>
      );
      break;
    case "CALCULADO":
      texto =
        "Revisa la vista previa: totales, comparación con el mes anterior y avisos por casa. Desde ahí se emiten las boletas.";
      botones = (
        <Link
          to={`/cierres/${cierre.id}/proforma`}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          <FileCheck2 className="h-4 w-4" strokeWidth={1.5} />
          Revisar y emitir boletas
          <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </Link>
      );
      break;
    case "FACTURADO":
      texto =
        "Las boletas están emitidas. Imprímelas por ruta y repártelas; cuando todo esté revisado, cierra el periodo.";
      botones = (
        <>
          <Button icon={Printer} onClick={irAImprimir}>
            Imprimir por ruta
          </Button>
          <Button variant="secondary" icon={PauseCircle} disabled={ocupado} onClick={() => setRevisionOpen(true)}>
            Enviar a revisión
          </Button>
          <Button variant="secondary" icon={Lock} disabled={ocupado} onClick={() => setConfirmCerrar(true)}>
            Cerrar periodo
          </Button>
        </>
      );
      break;
    case "HOLD":
      texto =
        "Un supervisor está revisando el periodo. Cuando termine, libera la revisión para poder cerrarlo.";
      botones = (
        <>
          <Button
            icon={PlayCircle}
            disabled={ocupado}
            onClick={() => ejecutar(liberarRevision, "Revisión terminada")}
          >
            {accion.isPending ? "Liberando…" : "Liberar revisión"}
          </Button>
          <Button variant="secondary" icon={Printer} onClick={irAImprimir}>
            Imprimir por ruta
          </Button>
        </>
      );
      break;
    case "EN_PREVALIDACION":
    case "EN_CALCULO":
    case "EN_FACTURACION":
      texto = `${ETIQUETA_ESTADO[cierre.estado]}… Actualiza en unos segundos.`;
      break;
    case "CERRADO":
      return (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Periodo cerrado: sus lecturas y boletas ya no se pueden modificar.
        </section>
      );
  }

  return (
    <section className="bg-white rounded-lg border border-neutral-200 p-4 space-y-3">
      <h2 className="text-sm font-semibold text-neutral-900">Próximo paso</h2>
      {texto && <p className="text-sm text-neutral-700">{texto}</p>}
      {botones && <div className="flex flex-wrap items-center gap-2">{botones}</div>}
      {accion.isPending && !confirmCerrar && <AvisoEspera />}

      <ConfirmDialog
        open={confirmCerrar}
        title={`Cerrar ${MESES[cierre.mes - 1]} ${cierre.anio}`}
        description={
          <>
            Cerrar el periodo lo sella: ya no se podrán modificar sus lecturas ni
            boletas. Se cierran {cierre.total_contratos.toLocaleString("es-CL")} contratos
            por un total facturado de <strong>{formatCLP(cierre.total_facturado)}</strong>.
          </>
        }
        confirmLabel="Cerrar periodo"
        loading={accion.isPending}
        loadingLabel="Cerrando periodo…"
        loadingHint={TEXTO_ESPERA}
        onConfirm={() => ejecutar(cerrarCierre, "Periodo cerrado", () => setConfirmCerrar(false))}
        onCancel={() => setConfirmCerrar(false)}
      />

      {revisionOpen && (
        <EnviarRevisionModal
          cierre={cierre}
          onClose={() => setRevisionOpen(false)}
          onDone={() => {
            setRevisionOpen(false);
            invalidar();
          }}
        />
      )}
    </section>
  );
}

function EnviarRevisionModal({
  cierre,
  onClose,
  onDone,
}: {
  cierre: CierreMensualRead;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => enviarARevision(cierre.id, motivo.trim()),
    onSuccess: () => {
      toast.success("Periodo enviado a revisión");
      onDone();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "No se pudo enviar a revisión");
    },
  });
  const valido = motivo.trim().length >= 3;

  return (
    <Modal
      open
      onClose={onClose}
      title="Enviar a revisión"
      subtitle={`${MESES[cierre.mes - 1]} ${cierre.anio}`}
      icon={PauseCircle}
      size="md"
      closeOnBackdrop={!mut.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            icon={PauseCircle}
            disabled={!valido || mut.isPending}
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
          >
            {mut.isPending ? "Enviando…" : "Enviar a revisión"}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-neutral-700">
        El periodo queda en revisión de un supervisor y no se puede cerrar hasta
        liberarlo. Las boletas emitidas no cambian.
      </p>
      <Textarea
        label="Qué hay que revisar"
        required
        rows={3}
        maxLength={2000}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        hint="Mínimo 3 caracteres. Queda registrado en la bitácora."
        error={error ?? undefined}
        autoFocus
      />
    </Modal>
  );
}

function BoletasImpresasPanel({ cierre }: { cierre: CierreMensualRead }): JSX.Element {
  const toast = useToast();
  const [descargando, setDescargando] = useState<string | null>(null);

  // Rutas del sector del cierre (o todas, si el cierre es global): una pila
  // de boletas por ruta es lo que cada operador se lleva a terreno.
  const rutasQ = useQuery({
    queryKey: ["rutas", "cierre", cierre.sector_id],
    queryFn: () =>
      listarRutas(
        cierre.sector_id != null
          ? { sector_id: cierre.sector_id, solo_activas: true }
          : { solo_activas: true },
      ),
  });

  const [confirmTodas, setConfirmTodas] = useState(false);
  // Un PDF grande corre en el servidor; recargar a mitad lo pierde.
  useAvisoSalida(descargando !== null);

  async function descargar(clave: string, rutaId?: number): Promise<void> {
    setDescargando(clave);
    try {
      await descargarBoletasPdf(cierre.id, rutaId);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo generar el PDF");
    } finally {
      setDescargando(null);
      setConfirmTodas(false);
    }
  }

  const hayRutas = (rutasQ.data?.length ?? 0) > 0;
  const boletas = cierre.lecturas_ingresadas;
  const nBoletas = boletas > 0 ? `${boletas.toLocaleString("es-CL")} ` : "";

  return (
    <section
      id={ID_PANEL_IMPRIMIR}
      tabIndex={-1}
      className="bg-white rounded-lg border border-neutral-200 p-4 space-y-3 scroll-mt-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div>
        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
          <Printer className="h-4 w-4" strokeWidth={1.5} />
          Imprimir boletas por ruta
        </h2>
        <p className="text-xs text-neutral-600 mt-0.5">
          Un PDF por ruta, con una boleta por página y en el mismo orden en que
          el operador las reparte. Es la forma rápida: cada ruta tarda unos
          segundos.
        </p>
      </div>

      {rutasQ.isPending && <Cargando inline />}

      {hayRutas && (
        <div className="flex flex-wrap gap-2">
          {rutasQ.data?.map((r) => {
            const clave = `ruta-${r.id}`;
            return (
              <Button
                key={r.id}
                variant="secondary"
                size="sm"
                icon={descargando === clave ? undefined : Printer}
                disabled={descargando !== null}
                onClick={() => void descargar(clave, r.id)}
                title={
                  r.operador_nombre
                    ? `Operador: ${r.operador_nombre}`
                    : "Sin operador asignado"
                }
              >
                {descargando === clave ? (
                  <EnCurso texto={`Generando ${r.codigo}…`} />
                ) : (
                  `${r.codigo} · ${r.nombre}`
                )}
                <span className="text-neutral-600 tabular-nums">
                  ({r.cantidad_arranques})
                </span>
              </Button>
            );
          })}
        </div>
      )}

      {!rutasQ.isPending && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-neutral-100 pt-3">
          <Button
            variant="secondary"
            size="sm"
            icon={Printer}
            disabled={descargando !== null}
            onClick={() => setConfirmTodas(true)}
          >
            Todas las boletas (PDF)
          </Button>
          <span className="text-xs text-neutral-600">
            {hayRutas
              ? "Un solo PDF con todo el periodo: puede tardar varios minutos."
              : "No hay rutas activas: se imprime un solo PDF, que puede tardar varios minutos."}
          </span>
        </div>
      )}

      <ConfirmDialog
        open={confirmTodas}
        tone="default"
        title="Imprimir todas las boletas"
        description={
          <>
            Se generará un solo PDF con las {nBoletas}boletas del periodo. Con
            muchas boletas puede tardar varios minutos y carga el servidor.
            {hayRutas && " Si vas a repartir, imprime por ruta: es más rápido."}
          </>
        }
        confirmLabel="Generar PDF"
        loading={descargando === "todas"}
        loadingLabel={`Generando ${nBoletas}boletas…`}
        loadingHint="Esto puede tardar varios minutos. No cierres ni recargues la página."
        onConfirm={() => void descargar("todas")}
        onCancel={() => setConfirmTodas(false)}
      />
    </section>
  );
}

function Counter({
  label,
  value,
  tone = "info",
}: {
  label: string;
  /** `null` = todavía no calculado (se muestra "—"). */
  value: number | null;
  tone?: "info" | "ok" | "warn" | "muted";
}): JSX.Element {
  const map: Record<typeof tone, string> = {
    info: "bg-white border-neutral-200 text-neutral-900",
    ok: "bg-emerald-50 border-emerald-100 text-emerald-900",
    warn: "bg-amber-50 border-amber-100 text-amber-900",
    muted: "bg-neutral-50 border-neutral-200 text-neutral-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tone]}`}>
      <div className="text-xs font-medium uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">
        {value === null ? (
          <span className="text-neutral-500" title="Se calcula al revisar las lecturas">
            —
          </span>
        ) : (
          value.toLocaleString("es-CL")
        )}
      </div>
    </div>
  );
}

function MoneyCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-lg border p-4 ${
        highlight
          ? "bg-brand-50 border-brand-200 text-brand-900"
          : "bg-white border-neutral-200 text-neutral-900"
      }`}
    >
      <div className="text-xs font-medium uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
